// JoinFunnel — /with/:slug
//
// A partner's branded front door. Their business name is on it, the company
// name is on it, and the visitor gives three things — name, email, phone —
// then lands directly in the secure intake. The client is created under the
// company and attributed to the partner the moment they submit.
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ShieldCheck, ArrowRight, Lock } from "lucide-react";

export default function JoinFunnelPage() {
  const { slug } = useParams();
  const [info, setInfo] = useState<any>(null);
  const [err, setErr] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [delivery, setDelivery] = useState<string | null>(null);
  const [consent, setConsent] = useState(false);
  const [website, setWebsite] = useState("");

  useEffect(() => {
    fetch(`/api/with/${slug}`)
      .then(async (r) => { const d = await r.json().catch(() => ({})); if (!r.ok) throw new Error(d?.message || "This link is not active."); setInfo(d); })
      .catch((e) => setErr(e.message));
  }, [slug]);

  async function go() {
    setBusy(true); setErr(null);
    try {
      const r = await fetch(`/api/with/${slug}`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim(), first_name: name.trim().split(/\s+/)[0], last_name: name.trim().split(/\s+/).slice(1).join(" "), email: email.trim(), phone: phone.trim() || undefined, consent, website: website || undefined }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d?.message || "Something went wrong — try again.");
      if (d.intake_url) { window.location.href = d.intake_url; return; }
      // Existing client: the secure link goes to the email on file, never to the browser.
      // delivery is "sent" | "failed" (null for the bot decoy — show the generic acknowledgement).
      // A "created" client with no link is the bot decoy: acknowledge generically, promise nothing.
      setDelivery(d.client_state === "existing" ? (d.delivery || "failed") : "generic");
      setSentTo(email.trim()); setBusy(false);
    } catch (e: any) { setErr(e.message); setBusy(false); }
  }

  const partner = info?.business_name as string | undefined;
  const partnerLogo = info?.logo_url as string | null | undefined;
  const company = info?.company_name as string | undefined;

  return (
    <div className="landing" style={{ minHeight: "100vh" }}>
      <header className="landing-nav">
        <div className="landing-shell landing-nav-inner">
          <span className="landing-logo" style={{ textDecoration: "none" }}>
            {company ? <>{company}</> : <>NO EXCUSES <em>Disputes</em></>}
          </span>
          {partner && (
            <span className="muted small" style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
              in partnership with
              {partnerLogo && (
                <span style={{ display: "grid", placeItems: "center", width: 30, height: 30, borderRadius: 7, background: "#fff", overflow: "hidden" }}>
                  <img src={partnerLogo} alt="" style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
                </span>
              )}
              <strong style={{ color: "var(--ne-text)" }}>{partner}</strong>
            </span>
          )}
        </div>
      </header>

      <section className="landing-section" style={{ paddingTop: 48 }}>
        <div className="landing-shell" style={{ maxWidth: 560 }}>
          {err && !info && (
            <div className="landing-card"><h1 style={{ fontSize: 22 }}>Link not active</h1><p className="muted">{err}</p></div>
          )}
          {info && sentTo && (
            <div className="landing-card" style={{ padding: 32 }}>
              {delivery === "generic" ? (
                <>
                  <p className="landing-kicker" style={{ marginBottom: 6 }}>Thank you</p>
                  <h1 style={{ fontSize: 26, lineHeight: 1.15, marginBottom: 10 }}>We received your details.</h1>
                  <p className="muted">Thanks for reaching out.</p>
                </>
              ) : delivery === "failed" ? (
                <>
                  <p className="landing-kicker" style={{ marginBottom: 6 }}>Almost there</p>
                  <h1 style={{ fontSize: 26, lineHeight: 1.15, marginBottom: 10 }}>We already have a file for {sentTo}.</h1>
                  <p className="muted">For your security the private intake link is only sent by email, and that email could not be delivered right now. Please contact {company || "the company"} directly and they will send your link.</p>
                </>
              ) : (
                <>
                  <p className="landing-kicker" style={{ marginBottom: 6 }}>Check your email</p>
                  <h1 style={{ fontSize: 26, lineHeight: 1.15, marginBottom: 10 }}>We already have a file for {sentTo}.</h1>
                  <p className="muted">For your security, your private intake link was sent to that address instead of opening here. Open the email and continue from there.</p>
                </>
              )}
            </div>
          )}
          {info && !sentTo && (
            <div className="landing-card" style={{ padding: 32 }}>
              {partnerLogo && (
                <span style={{ display: "grid", placeItems: "center", width: 64, height: 64, borderRadius: 12, background: "#fff", overflow: "hidden", marginBottom: 14 }}>
                  <img src={partnerLogo} alt={`${partner} logo`} style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
                </span>
              )}
              <p className="landing-kicker" style={{ marginBottom: 6 }}>{partner ? `Referred by ${partner}` : "Credit repair"}</p>
              <h1 style={{ fontSize: 28, lineHeight: 1.15, marginBottom: 10 }}>Let's start fixing your credit.</h1>
              <p className="muted" style={{ marginBottom: 22 }}>
                Three quick details to get started, then a secure intake that takes about ten minutes.
                Getting started does <strong>not</strong> affect your credit score.
              </p>

              <div className="stacked-form">
                <label className="form-field"><span>Full name</span>
                  <input value={name} onChange={(e) => setName(e.target.value)} placeholder="First and last name" autoComplete="name" /></label>
                <label className="form-field"><span>Email</span>
                  <input value={email} onChange={(e) => setEmail(e.target.value)} inputMode="email" autoComplete="email" placeholder="you@example.com" /></label>
                <label className="form-field"><span>Phone</span>
                  <input value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="tel" autoComplete="tel" placeholder="(555) 555-0142" /></label>
              </div>

              {/* Honeypot: hidden from people, filled by bots (same as the company intake page). */}
              <div aria-hidden="true" style={{ position: "absolute", left: -9999, width: 1, height: 1, overflow: "hidden" }}>
                <label>Website <input tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} /></label>
              </div>
              <label className="small" style={{ display: "flex", gap: 8, alignItems: "flex-start", marginTop: 14 }}>
                <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} style={{ marginTop: 3 }} />
                <span>I agree that {company || "the company"} may contact me by email, text or phone about my request.</span>
              </label>

              {err && <p className="error-text" style={{ marginTop: 10 }}>{err}</p>}
              <button className="primary-button" style={{ width: "100%", justifyContent: "center", marginTop: 18 }}
                disabled={busy || !consent || name.trim().split(/\s+/).length < 2 || !email.includes("@")} onClick={go}>
                {busy ? "Setting up your intake…" : <>Start my intake <ArrowRight size={16} aria-hidden="true" /></>}
              </button>

              <p className="muted small" style={{ marginTop: 16, display: "flex", gap: 8, alignItems: "flex-start" }}>
                <Lock size={14} aria-hidden="true" style={{ flex: "0 0 auto", marginTop: 2 }} />
                <span>Sent over an encrypted HTTPS connection. Your information is used only for your credit file and is never sold.</span>
              </p>
              <p className="muted small" style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                <ShieldCheck size={14} aria-hidden="true" style={{ flex: "0 0 auto", marginTop: 2 }} />
                <span>{company} cannot promise specific results. You always have the right to dispute inaccurate information yourself, free of charge, directly with the credit bureaus.</span>
              </p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
